import React, { useState, useRef } from 'react';
import { Upload, Link, Video, Search, Download, AlertCircle, CheckCircle, Loader } from 'lucide-react';

export default function FrameHuntApp() {
  const [inputType, setInputType] = useState('url'); // 'url' or 'upload'
  const [videoUrl, setVideoUrl] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [threshold, setThreshold] = useState(2.5);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState('');
  const [suspects, setSuspects] = useState([]);
  const [error, setError] = useState('');
  const [videoInfo, setVideoInfo] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('video/')) {
        setError('Please select a valid video file');
        return;
      }
      setVideoFile(file);
      setError('');
    }
  };

  const processVideo = async () => {
    setProcessing(true);
    setProgress('Starting analysis...');
    setError('');
    setSuspects([]);
    setVideoInfo(null);

    try {
      // Prepare form data
      const formData = new FormData();
      formData.append('threshold', threshold.toString());

      if (inputType === 'upload') {
        if (!videoFile) {
          setError('Please select a video file');
          setProcessing(false);
          return;
        }
        setProgress('Uploading video...');
        formData.append('video', videoFile);
      } else {
        if (!videoUrl.trim()) {
          setError('Please enter a video URL');
          setProcessing(false);
          return;
        }
        setProgress('Downloading video from URL...');
        formData.append('url', videoUrl);
      }

      // Call backend API
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      
      setProgress('Processing video (this may take a few minutes)...');
      
      const response = await fetch(`${API_URL}/api/process`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }

      const result = await response.json();
      
      setProgress('Analysis complete!');
      
      setVideoInfo({
        totalFrames: result.totalFrames,
        meanDiffScore: result.meanDiffScore,
        stdDeviation: result.stdDeviation,
        suspectCount: result.suspectCount
      });
      
      setSuspects(result.suspects);
      setProgress('');
      
    } catch (err) {
      setError(err.message || 'An error occurred during processing');
      setProgress('');
    } finally {
      setProcessing(false);
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const extractVideoName = (url) => {
    try {
      const urlObj = new URL(url);
      const path = urlObj.pathname;
      const name = path.split('/').pop() || 'video';
      return name.replace(/\.[^.]*$/, '');
    } catch {
      return 'video';
    }
  };

  const downloadSuspect = (suspect, index) => {
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${suspect.imageBase64}`;
    link.download = `suspect_${index + 1}_frame${suspect.frameNumber}_score${suspect.score.toFixed(1)}.png`;
    link.click();
  };

  const downloadAllSuspects = () => {
    suspects.forEach((suspect, index) => {
      setTimeout(() => downloadSuspect(suspect, index), index * 100);
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Search className="w-12 h-12 text-purple-400" />
            <h1 className="text-5xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              FrameHunt
            </h1>
          </div>
          <p className="text-slate-300 text-lg">
            Discover hidden easter eggs in your videos using AI-powered frame analysis
          </p>
        </div>

        {/* Input Section */}
        <div className="bg-slate-800/50 backdrop-blur rounded-2xl p-8 mb-8 border border-slate-700">
          <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
            <Video className="w-6 h-6 text-purple-400" />
            Video Input
          </h2>

          {/* Input Type Toggle */}
          <div className="flex gap-4 mb-6">
            <button
              onClick={() => setInputType('url')}
              className={`flex-1 py-3 px-6 rounded-lg font-medium transition-all ${
                inputType === 'url'
                  ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/50'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              <Link className="w-5 h-5 inline mr-2" />
              Video URL
            </button>
            <button
              onClick={() => setInputType('upload')}
              className={`flex-1 py-3 px-6 rounded-lg font-medium transition-all ${
                inputType === 'upload'
                  ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/50'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              <Upload className="w-5 h-5 inline mr-2" />
              Upload File
            </button>
          </div>

          {/* URL Input */}
          {inputType === 'url' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Video URL (YouTube, direct link, etc.)
              </label>
              <input
                type="text"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                placeholder="https://youtube.com/watch?v=..."
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
              />
            </div>
          )}

          {/* File Upload */}
          {inputType === 'upload' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Upload Video File
              </label>
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-slate-600 rounded-lg p-8 text-center cursor-pointer hover:border-purple-500 transition-colors"
              >
                <Upload className="w-12 h-12 mx-auto mb-3 text-slate-400" />
                {videoFile ? (
                  <p className="text-purple-400 font-medium">{videoFile.name}</p>
                ) : (
                  <p className="text-slate-400">Click to select a video file</p>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          )}

          {/* Threshold Slider */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Sensitivity Threshold: {threshold} std devs
            </label>
            <input
              type="range"
              min="1"
              max="5"
              step="0.1"
              value={threshold}
              onChange={(e) => setThreshold(parseFloat(e.target.value))}
              className="w-full accent-purple-500"
            />
            <div className="flex justify-between text-xs text-slate-400 mt-1">
              <span>More sensitive (more suspects)</span>
              <span>Less sensitive (fewer suspects)</span>
            </div>
          </div>

          {/* Process Button */}
          <button
            onClick={processVideo}
            disabled={processing}
            className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg font-semibold text-lg hover:shadow-lg hover:shadow-purple-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {processing ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Search className="w-5 h-5" />
                Hunt for Easter Eggs
              </>
            )}
          </button>
        </div>

        {/* Progress */}
        {progress && (
          <div className="bg-blue-900/30 border border-blue-500 rounded-lg p-4 mb-8 flex items-center gap-3">
            <Loader className="w-5 h-5 animate-spin text-blue-400" />
            <span className="text-blue-200">{progress}</span>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-900/30 border border-red-500 rounded-lg p-4 mb-8 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-200">{error}</span>
          </div>
        )}

        {/* Video Info */}
        {videoInfo && (
          <div className="bg-slate-800/50 backdrop-blur rounded-2xl p-6 mb-8 border border-slate-700">
            <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-green-400" />
              Analysis Complete
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-700/50 rounded-lg p-4">
                <p className="text-slate-400 text-sm">Total Frames</p>
                <p className="text-2xl font-bold text-purple-400">{videoInfo.totalFrames}</p>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-4">
                <p className="text-slate-400 text-sm">Suspects Found</p>
                <p className="text-2xl font-bold text-pink-400">{videoInfo.suspectCount}</p>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-4">
                <p className="text-slate-400 text-sm">Mean Diff</p>
                <p className="text-2xl font-bold text-blue-400">{videoInfo.meanDiffScore.toFixed(2)}</p>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-4">
                <p className="text-slate-400 text-sm">Std Dev</p>
                <p className="text-2xl font-bold text-green-400">{videoInfo.stdDeviation.toFixed(2)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Suspects Grid */}
        {suspects.length > 0 && (
          <div className="bg-slate-800/50 backdrop-blur rounded-2xl p-8 border border-slate-700">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-semibold">Suspect Frames ({suspects.length})</h3>
              <button
                onClick={downloadAllSuspects}
                className="px-4 py-2 bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download All
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {suspects.map((suspect, index) => (
                <div key={index} className="bg-slate-700/50 rounded-lg overflow-hidden border border-slate-600 hover:border-purple-500 transition-colors">
                  <img
                    src={`data:image/png;base64,${suspect.imageBase64}`}
                    alt={`Suspect frame ${suspect.frameNumber}`}
                    className="w-full aspect-video object-cover"
                  />
                  <div className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="text-sm text-slate-400">Frame #{suspect.frameNumber}</p>
                        <p className="text-lg font-semibold text-purple-400">
                          Score: {suspect.score.toFixed(2)}
                        </p>
                      </div>
                      <span className="px-2 py-1 bg-pink-600/30 text-pink-300 rounded text-xs font-medium">
                        {suspect.stdDeviations.toFixed(1)}σ
                      </span>
                    </div>
                    <button
                      onClick={() => downloadSuspect(suspect, index)}
                      className="w-full py-2 bg-slate-600 hover:bg-slate-500 rounded transition-colors flex items-center justify-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!processing && suspects.length === 0 && !error && !progress && (
          <div className="text-center py-16 text-slate-400">
            <Search className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p className="text-lg">Enter a video URL or upload a file to start hunting for easter eggs</p>
          </div>
        )}
      </div>
    </div>
  );
}